import { call, put, takeLatest } from 'redux-saga/effects'
import {
  FETCH_USER_DETAILS_REQUEST,
  fetchUserDetailsFailure,
  fetchUserDetailsSuccess,
  setSelectedCompany,
  setSelectedStore,
} from './action'
import { MENU, ROLE, USER_TYPES } from '@common/src/constants/constants'
import { MENUITEMS } from '../../../shared/components/sidebar/sidemenu/sidemenu'
import { getUser } from '../../../service/firebase/user'
import { getCompany } from '../../../service/firebase/company'
import { getStore } from '../../../service/firebase/store'
import { getRole } from '../../../service/firebase/roles'
import { ALL_OPTION } from '@pulse/constants/constants'
import { currentRegion, isRegionAvailable, regionStorageKey, switchRegion } from '@common/src/config/regions'

const filterMenuItems = (menuItems, permissions) => {
  const allowedKeys = new Set(permissions)
  const filterItems = (items) => {
    return items.reduce((acc, item) => {
      if (item.type === MENU.TYPE_LINK) {
        if (allowedKeys.has(item.key) || Array.from(allowedKeys).some((el) => el.startsWith(item.key))) {
          acc.push(item)
        }
      } else if (item.type === MENU.TYPE_SUB && item.children) {
        const filteredChildren = filterItems(item.children)
        if (filteredChildren.length > 0) {
          acc.push({
            ...item,
            children: filteredChildren,
          })
        }
      }
      return acc
    }, [])
  }

  return filterItems(menuItems)
}

function* fetchUserDetailsSaga(action) {
  try {
    const { uid } = action
    const user = yield call(getUser, uid)
    if (!user) {
      yield put(fetchUserDetailsFailure('No user data found!'))
      return
    }
    // Region for this user: sub-admins work in the region stored on their user doc; admins only manage global
    // data (users/roles) and work in home. If it isn't the region this page load runs in, save it and reload so
    // every client is created for it.
    const isAdmin = user.userType === USER_TYPES.HUB_ADMIN || user.userType === USER_TYPES.PULSE_ADMIN
    const region = isAdmin ? 'home' : user.region || 'home'
    if (!isRegionAvailable(region)) {
      yield put(fetchUserDetailsFailure(`Region "${region}" is not available in this environment.`))
      return
    }
    if (region !== currentRegion.key) {
      if (!switchRegion(region)) {
        // Never continue in the wrong region (e.g. browser storage blocked, so the region can't be saved).
        yield put(fetchUserDetailsFailure(`Could not switch to region "${region}". Please enable site storage.`))
      }
      return // reloading into the user's region
    }

    let permissions
    let companies = []
    let stores = []

    if (user.userType === USER_TYPES.HUB_ADMIN || user.userType === USER_TYPES.PULSE_ADMIN) {
       permissions = [
        {
          roleData: {
            permissions: ["accountsPulse"]
          }
        }
      ]
      companies = []
      stores = []
    } else {
      permissions = yield call(getRole, uid)
      if (import.meta.env.VITE_PORTAL === ROLE.ROLE_TYPE_HUB) {
        const hubRole = permissions.find((perm) => perm.roleData?.type === ROLE.ROLE_TYPE_HUB)
        companies = yield call(getCompany, hubRole?.allCompanies, hubRole?.companies)
      }
      if (import.meta.env.VITE_PORTAL === ROLE.ROLE_TYPE_PULSE) {
        const hubRole = permissions.find((perm) => perm.roleData?.type === ROLE.ROLE_TYPE_PULSE)
        stores = yield call(getStore, hubRole?.allStores, hubRole?.stores)
      }
    }
    let menuItems = MENUITEMS

    if (permissions !== 'all') {
      const userPermission = [...new Set(permissions.map((p) => p.roleData.permissions).flat())]
      menuItems = filterMenuItems(MENUITEMS, userPermission)
      permissions = userPermission
    } else if (user.userType === USER_TYPES.PULSE_ADMIN) {
      menuItems = MENUITEMS.filter((item) => item.pulseAdmin)
    }
    yield put(fetchUserDetailsSuccess(user, permissions, companies, menuItems, stores))

    const selectedCompanyKey = regionStorageKey('selectedCompany')
    const selectedStoreKey = regionStorageKey('selectedStore')
    const selectedCompany =
      localStorage.getItem(selectedCompanyKey) === null
        ? companies?.[0]
        : JSON.parse(localStorage.getItem(selectedCompanyKey))

    const selectedStore =
      localStorage.getItem(selectedStoreKey) === null ? ALL_OPTION.store : JSON.parse(localStorage.getItem(selectedStoreKey))

    // also check selected company value is in companies array otherwise take first one
    // if (!companies.find((c) => c.value === selectedCompany.value)) {
    //   selectedCompany = companies[0]
    // }
    yield put(setSelectedCompany(selectedCompany))
    yield put(setSelectedStore(selectedStore))
  } catch (error) {
    yield put(fetchUserDetailsFailure(error.message))
  }
}

function* watchFetchUser() {
  yield takeLatest(FETCH_USER_DETAILS_REQUEST, fetchUserDetailsSaga)
}

export default watchFetchUser
